import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/footer")({
  head: () => ({ meta: [{ title: "Footer — Admin" }] }),
  component: AdminFooter,
});

type Link = { label: string; href: string };
type Settings = {
  id?: string;
  brand_name: string;
  brand_tagline: string;
  shop_title: string;
  shop_links: Link[];
  help_title: string;
  help_links: Link[];
  newsletter_title: string;
  newsletter_text: string;
  copyright_text: string;
  instagram_url: string;
  youtube_url: string;
};

function AdminFooter() {
  const [s, setS] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from("footer_settings").select("*").maybeSingle();
      if (error) return toast.error(error.message);
      if (data) setS({
        id: data.id,
        brand_name: data.brand_name,
        brand_tagline: data.brand_tagline,
        shop_title: data.shop_title,
        shop_links: data.shop_links as Link[],
        help_title: data.help_title,
        help_links: data.help_links as Link[],
        newsletter_title: data.newsletter_title,
        newsletter_text: data.newsletter_text,
        copyright_text: data.copyright_text,
        instagram_url: (data as { instagram_url?: string }).instagram_url ?? "",
        youtube_url: (data as { youtube_url?: string }).youtube_url ?? "",
      });
    })();
  }, []);

  if (!s) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const update = (patch: Partial<Settings>) => setS({ ...s, ...patch });
  const updateLink = (key: "shop_links" | "help_links", i: number, patch: Partial<Link>) => {
    const arr = [...s[key]];
    arr[i] = { ...arr[i], ...patch };
    update({ [key]: arr } as Partial<Settings>);
  };
  const addLink = (key: "shop_links" | "help_links") =>
    update({ [key]: [...s[key], { label: "", href: "" }] } as Partial<Settings>);
  const removeLink = (key: "shop_links" | "help_links", i: number) =>
    update({ [key]: s[key].filter((_, idx) => idx !== i) } as Partial<Settings>);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("footer_settings")
      .update({
        brand_name: s.brand_name,
        brand_tagline: s.brand_tagline,
        shop_title: s.shop_title,
        shop_links: s.shop_links,
        help_title: s.help_title,
        help_links: s.help_links,
        newsletter_title: s.newsletter_title,
        newsletter_text: s.newsletter_text,
        copyright_text: s.copyright_text,
        instagram_url: s.instagram_url,
        youtube_url: s.youtube_url,
      } as never)
      .eq("id", s.id!);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Footer saved");
  };

  const input = "w-full border border-border bg-background px-3 py-2 rounded text-sm";
  const ta = input + " min-h-[80px]";

  const LinksEditor = ({ k, title }: { k: "shop_links" | "help_links"; title: string }) => (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      {s[k].map((l, i) => (
        <div key={i} className="flex gap-2">
          <input className={input} placeholder="Label" value={l.label}
            onChange={(e) => updateLink(k, i, { label: e.target.value })} />
          <input className={input} placeholder="URL (optional)" value={l.href}
            onChange={(e) => updateLink(k, i, { href: e.target.value })} />
          <button onClick={() => removeLink(k, i)} className="p-2 border border-border rounded hover:bg-muted">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button onClick={() => addLink(k)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-border rounded hover:bg-muted">
        <Plus className="h-3.5 w-3.5" /> Add link
      </button>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="display text-xl">Footer</h2>
        <p className="text-sm text-muted-foreground">Customize every element of the site footer.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <p className="text-sm font-medium">Brand</p>
        <input className={input} placeholder="Brand name" value={s.brand_name}
          onChange={(e) => update({ brand_name: e.target.value })} />
        <textarea className={ta} placeholder="Tagline" value={s.brand_tagline}
          onChange={(e) => update({ brand_tagline: e.target.value })} />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <input className={input} value={s.shop_title}
          onChange={(e) => update({ shop_title: e.target.value })} />
        <LinksEditor k="shop_links" title="Shop links" />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <input className={input} value={s.help_title}
          onChange={(e) => update({ help_title: e.target.value })} />
        <LinksEditor k="help_links" title="Help links" />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <p className="text-sm font-medium">Newsletter</p>
        <input className={input} value={s.newsletter_title}
          onChange={(e) => update({ newsletter_title: e.target.value })} />
        <textarea className={ta} value={s.newsletter_text}
          onChange={(e) => update({ newsletter_text: e.target.value })} />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <p className="text-sm font-medium">Social links</p>
        <p className="text-xs text-muted-foreground">Shown as icons in the site footer. Leave blank to hide.</p>
        <label className="block text-xs text-muted-foreground">Instagram profile URL</label>
        <input className={input} placeholder="https://www.instagram.com/yourhandle" value={s.instagram_url}
          onChange={(e) => update({ instagram_url: e.target.value })} />
        <label className="block text-xs text-muted-foreground">YouTube channel URL</label>
        <input className={input} placeholder="https://www.youtube.com/@yourchannel" value={s.youtube_url}
          onChange={(e) => update({ youtube_url: e.target.value })} />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-2">
        <p className="text-sm font-medium">Copyright</p>
        <p className="text-xs text-muted-foreground">Use <code>{"{year}"}</code> for the current year.</p>
        <input className={input} value={s.copyright_text}
          onChange={(e) => update({ copyright_text: e.target.value })} />
      </div>

      <button onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-foreground text-background rounded text-sm disabled:opacity-40">
        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save footer"}
      </button>
    </div>
  );
}
